import { useEffect, lazy, Suspense, useMemo, useCallback, useRef } from 'react';
import { useSRZId } from '@/hooks';
import { AutoTooltip, LoadingSpinner } from '@/components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserFeedbackIcon } from '@/components/Icons';
import { useFeedbackEnabled } from '@/features/feedback/hooks/useFeedbackEnabled';
import clsx from 'clsx';
import { DetailsPanelWrapper } from '@/components/DetailsPanel/DetailsPanelWrapper';
import { isDev } from '@/constants';
import { Tabs, theme } from 'antd';
import { PageLayoutMainContent } from '@/components/PageLayout';
import { gql } from '@apollo/client';
import {
  useMainboardRoomsQuery,
  useMainboardSpaceQuery,
  useMainboardZonesQuery,
  useMainboardBuildingQuery,
} from '@/.generated/graphql';
import toast from 'react-hot-toast';
import { formatOperationError } from '@/api/formatError';
import { useFeatureGate } from '@statsig/react-bindings';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';
import TimeDisplay from '@/components/TimeDisplay';
import FloorSetupCard from '@/pages/LivePage/Floor/FloorSetupCard';
import RoomSetupCard from '@/pages/LivePage/Room/RoomSetupCard';

const DeviceManagementPage = lazy(() => import('@/pages/DeviceManagementPage'));
const Dashboard = lazy(() => import('../Dashboard'));
const ScopingToolPage = lazy(() => import('@/pages/StudioPage'));
const DemoPage = lazy(() => import('@/pages/DemoPage'));
const LivePageContainer = lazy(() => import('@/pages/LivePage/LivePageContainer'));

interface IMainboardProps {
  isRoom: boolean;
  isZone: boolean;
}

// When adding / removing a tab, update THREE places in lockstep:
//   1. `TAB_ROUTES` (below) — the tab → URL map.
//   2. `isTabValidForContext` — the show-in-sidebar rules.
//   3. `tabItems` in the Mainboard component — the visible order.
// The `if (!route) { ... }` bail in the restore effect is a defensive
// net for drift between these, but don't rely on it: a tab missing
// from `TAB_ROUTES` will silently fail restore for any user who had
// persisted it.
export const TAB_ROUTES: Record<string, string> = {
  live: '/live',
  historical: '/',
  studio: '/studio',
  devices: '/devices',
  demo: '/demo',
};

/**
 * Derive which Mainboard tab is active from a pathname. Exported for unit
 * testing; the historical tab is the fallback for any unrecognized path.
 */
export function getActiveTabKey(pathname: string): string {
  if (pathname === '/live' || pathname.startsWith('/live/')) return 'live';
  if (pathname === '/studio' || pathname.startsWith('/studio/')) return 'studio';
  if (pathname === '/devices' || pathname.startsWith('/devices/')) return 'devices';
  if (pathname === '/demo' || pathname.startsWith('/demo/')) return 'demo';
  return 'historical';
}

export const ACTIVE_TAB_STORAGE_KEY = 'butlr-active-tab';

// sessionStorage value is `${userId}:${tab}` — scoped by user so that on a
// shared browser / support-impersonation flow (user A logs out, user B logs
// in in the same tab) user B doesn't get user A's tab restored. For
// cross-user matches isTabValidForContext would usually reject the restore
// anyway, but only when the two users' access patterns differ; if both can
// see the same tab types, the previous user's choice would leak through.
function encodeStoredTab(userId: string, tab: string): string {
  return `${userId}:${tab}`;
}

function decodeStoredTab(raw: string | null, userId: string | null): string | null {
  if (!raw || !userId) return null;
  // Split on the LAST `:` so colon-bearing user ids (e.g. enterprise SSO
  // subs like `okta:Conn:xx` if they ever make it into currentUserId)
  // survive round-trip. TAB_ROUTES keys are single words, so the tail
  // never contains `:`.
  const idx = raw.lastIndexOf(':');
  if (idx <= 0) return null;
  const storedUser = raw.slice(0, idx);
  if (storedUser !== userId) return null;
  return raw.slice(idx + 1) || null;
}

/**
 * Whether a given tab key can be shown in the current sidebar context. Mirrors
 * the filtering in `tabItems` below; factored out so sidebar navigation and
 * landing-page redirects can agree on what "valid" means.
 */
export function isTabValidForContext(
  tab: string,
  ctx: { spaceId?: string | null; buildingId?: string | null; livePageEnabled?: boolean },
): boolean {
  switch (tab) {
    case 'live':
      return !!ctx.livePageEnabled && !!ctx.spaceId;
    case 'studio':
      return !ctx.buildingId;
    case 'historical':
    case 'devices':
      return true;
    case 'demo':
      return isDev;
    default:
      return false;
  }
}

/**
 * Read the last-chosen tab from sessionStorage, scoped to the given user.
 * Returns null when unset, unreadable, or stored for a different user.
 */
export function readStoredTab(userId: string | null): string | null {
  try {
    return decodeStoredTab(sessionStorage.getItem(ACTIVE_TAB_STORAGE_KEY), userId);
  } catch {
    return null;
  }
}

/**
 * Persist the chosen tab, scoped to the given user. Silent-fails in
 * restricted environments and when userId isn't yet known (before `me`
 * query resolves) — re-clicking the tab once logged-in will persist.
 */
export function writeStoredTab(userId: string | null, tab: string): void {
  if (!userId) return;
  try {
    sessionStorage.setItem(ACTIVE_TAB_STORAGE_KEY, encodeStoredTab(userId, tab));
  } catch {
    /* sessionStorage may be unavailable in private-mode Safari / restricted iframes */
  }
}

// FIXME: this component is a redundant abstraction with OrganizationPage,
// the only component which renders it. Pieces should be pulled out into
// sub-components and bundled up into OrganizationPage.
export const Mainboard = ({ isRoom, isZone }: IMainboardProps) => {
  const { token } = theme.useToken();
  const { search, pathname } = useLocation();
  const { spaceId, roomId, zoneId, buildingId } = useSRZId();
  const { value: livePageEnabled } = useFeatureGate('live_page');
  const newFeedbackEnabled = useFeedbackEnabled();
  const { currentUserId, isLoading: isAccessLoading } = useSpaceAccess();

  const { error: buildingErrorData } = useMainboardBuildingQuery({
    variables: {
      // asserted by skip below

      buildingId: buildingId!,
    },
    skip: !buildingId,
  });
  const { data: spaceData, error: spaceErrorData } = useMainboardSpaceQuery({
    variables: {
      // asserted by skip below

      spaceId: spaceId!,
    },
    skip: !spaceId,
  });
  const space = spaceData?.floors.data?.[0] ?? null;

  const { data: roomData, error: roomErrorData } = useMainboardRoomsQuery({
    variables: {
      // asserted by skip below

      roomId: roomId!,
    },
    skip: !spaceId || !roomId,
  });
  const room = roomData?.rooms?.data?.[0] ?? null;

  const { data: zoneData, error: zoneErrorData } = useMainboardZonesQuery({
    variables: {
      // asserted by skip below

      zoneId: zoneId!,
    },
    skip: !spaceId || !zoneId,
  });
  const zone = zoneData?.zones?.data?.[0] ?? null;

  const { t } = useTranslation();

  const renderError = () => {
    const errorEntries: [string, typeof buildingErrorData][] = [
      ['load building', buildingErrorData],
      ['load space', spaceErrorData],
      ['load room', roomErrorData],
      ['load zone', zoneErrorData],
    ];
    for (const [operation, errorData] of errorEntries) {
      if (errorData) {
        return formatOperationError(operation, errorData);
      }
    }
    return 'Failed to load data';
  };

  const hasData = space || room || zone;
  const hasError = !!spaceErrorData || !!roomErrorData || !!zoneErrorData;
  // if there's data available, but there's an error, show a toast instead
  // of blocking the whole view. If there's no data, we render a full
  // error page below.
  const toastErrorMessage = hasError && hasData ? renderError() : null;
  useEffect(() => {
    if (toastErrorMessage) {
      toast.error(toastErrorMessage, {
        id: 'dashboard-error',
      });
    }
  }, [toastErrorMessage]);
  const navigate = useNavigate();

  const activeTabKey = useMemo(() => getActiveTabKey(pathname), [pathname]);
  // Remount when the selected building/space/room/zone changes so stale data
  // from the previous selection doesn't bleed into the new one.
  const remountKey = useMemo(
    () => [buildingId, spaceId, roomId, zoneId].filter(Boolean).join(''),
    [buildingId, spaceId, roomId, zoneId],
  );

  const tabItems = useMemo(() => {
    const items: { key: string; label: string }[] = [];
    if (livePageEnabled && spaceId) {
      items.push({ key: 'live', label: t('live') });
    }
    items.push({ key: 'historical', label: 'Historical' });
    // Studio and Devices have been moved out of the horizontal tabs and
    // into the left sidebar's module switcher. Keep the routes/components
    // intact — they are rendered below based on activeTabKey.
    if (isDev) {
      items.push({ key: 'demo', label: 'Demo' });
    }
    return items;
  }, [livePageEnabled, spaceId, t]);

  // Restore the last-chosen tab when the user lands on the historical fallback
  // (path `/`) after sidebar navigation or a building click reset the route.
  // The guard fires at most once per mount; re-evaluates on context changes
  // until one of:
  //   - pathname changes (user navigated — close the restore window)
  //   - no stored tab / stored == historical (nothing to restore)
  //   - stored tab is valid for the current context (fire restore)
  //   - user explicitly clicks a tab (handleTabChange flips the guard)
  // If the stored tab is currently invalid (e.g. Statsig `live_page` still
  // bootstrapping, returning false), we do NOT flip the guard — the effect
  // retries when deps change, catching the restore once the gate flips true.
  //
  // `search` is read via a ref rather than declared as a dep so unrelated
  // query-param writes while pathname='/' don't re-trigger the restore check.
  // The render-time `searchRef.current = search` assignment is the React-docs
  // endorsed pattern for syncing a ref to the latest prop/state value — refs
  // have no subscribers, and a discarded concurrent render would re-assign
  // the same value on the next attempt, so there's no torn-state risk.
  const didRestoreTabRef = useRef(false);
  const searchRef = useRef(search);
  searchRef.current = search;

  const handleTabChange = useCallback(
    (key: string) => {
      writeStoredTab(currentUserId, key);
      // Close the restoration window on any explicit tab click. The
      // restore effect normally closes itself when pathname leaves '/',
      // but a click on the historical tab navigates to '/' (no pathname
      // change), which would leave the window open. If livePageEnabled
      // (or another deferred-validity gate) flips true on a later
      // render, the deferred restore could otherwise override the
      // user's choice with the previously-stored tab.
      didRestoreTabRef.current = true;
      navigate((TAB_ROUTES[key] || '/') + search);
    },
    [navigate, search, currentUserId],
  );
  useEffect(() => {
    if (didRestoreTabRef.current) return;
    if (pathname !== '/') {
      // User is already on a real tab (possibly from this same restore);
      // close the window so later dep changes don't re-fire.
      didRestoreTabRef.current = true;
      return;
    }
    // If currentUserId isn't known yet (me query still loading), defer the
    // restore — readStoredTab would refuse to decode without a user to
    // match against. The effect re-evaluates when currentUserId arrives.
    if (!currentUserId) {
      return;
    }
    // Also wait for access state to finish loading. Otherwise the restore
    // effect can race SpaceAccessGuard: we'd navigate to
    // /studio?spaceId=<restricted> while the guard decides the URL is
    // forbidden and bounces to `/`. Functionally the guard still wins,
    // but the user sees two navigations and a flash of the forbidden
    // route. Deferring until access is known keeps the sequence clean.
    if (isAccessLoading) {
      return;
    }
    // NOTE: no pre-check on `hasAccess('floor', spaceId)` here. An
    // earlier version of this code bailed if the URL's spaceId wasn't
    // directly accessible — but `hasAccess` walks upward from the
    // queried space (floor → building → site), so a Member whose only
    // grant is a room/zone returns false for `hasAccess('floor', X)`
    // even when they can legitimately visit `?spaceId=X&roomId=<leaf>`
    // via their leaf grant. That bail caused the Brigid scenario's
    // stored tab to be silently discarded. SpaceAccessGuard + the
    // isTabValidForContext check below are the right gates; trust them.
    const stored = readStoredTab(currentUserId);
    if (!stored || stored === 'historical') {
      didRestoreTabRef.current = true;
      return;
    }
    if (!isTabValidForContext(stored, { spaceId, buildingId, livePageEnabled })) {
      // Leave the guard open — Statsig bootstrap or sidebar context hydration
      // might flip this to valid on the next render.
      return;
    }
    // Defensive lookup: isTabValidForContext and TAB_ROUTES must stay in
    // sync, but if a future contributor adds a valid tab to the former
    // without updating the latter, navigate(undefined + ...) would
    // produce a literal "undefined?..." URL. Bail instead.
    const route = TAB_ROUTES[stored];
    if (!route) {
      didRestoreTabRef.current = true;
      return;
    }
    // Flip the guard BEFORE navigate to prevent a re-entrant effect run
    // observing an open guard on the pre-commit render, but roll back on
    // navigate failure so the next dep-change (or a manual reload) can
    // retry. In practice router v5's navigate doesn't throw, but
    // unmount / router-remount edge cases have surfaced on other
    // branches and this costs nothing.
    didRestoreTabRef.current = true;
    try {
      // `searchRef.current` is read here intentionally instead of the
      // `search` closure value — the ref is synced on every render via
      // `searchRef.current = search` above, so we always pick up the
      // latest search even though the effect intentionally doesn't
      // depend on `search`. See the block comment above the refs.
      navigate(route + searchRef.current, { replace: true });
    } catch {
      didRestoreTabRef.current = false;
    }
  }, [pathname, spaceId, buildingId, livePageEnabled, navigate, currentUserId, isAccessLoading]);

  return (
    <div
      className={clsx('flex flex-grow flex-col min-h-0')}
      style={{ backgroundColor: token.colorBgLayout }}
    >
      {!hasData && hasError ? (
        <div className="flex h-full w-full items-center justify-center text-xl">
          {renderError()}
        </div>
      ) : (
        <>
          {/* Sensor setup ("Installed: …" + What's Enabled): rendered under
            the floor / room title on data-centric tabs only. Hidden on
            Studio and Devices — Studio's canvas already uses the full
            viewport, and Devices has its own per-sensor table that
            duplicates this summary. */}
          {activeTabKey !== 'studio' && activeTabKey !== 'devices' && (
            <>
              {spaceId && !roomId && !zoneId && !buildingId && <FloorSetupCard spaceId={spaceId} />}
              {spaceId && roomId && !zoneId && !buildingId && (
                <RoomSetupCard roomId={roomId} spaceId={spaceId} />
              )}
            </>
          )}
          {activeTabKey !== 'studio' && activeTabKey !== 'devices' && (
            <div className="px-6" style={{ backgroundColor: token.colorBgLayout }}>
              <Tabs
                activeKey={activeTabKey}
                onChange={handleTabChange}
                items={tabItems}
                tabBarExtraContent={{
                  right: (
                    <span
                      style={{
                        fontSize: token.fontSizeSM,
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      <span style={{ color: token.colorTextTertiary, marginRight: 4 }}>
                        Local Time:
                      </span>
                      <TimeDisplay style={{ color: token.colorText }} />
                    </span>
                  ),
                }}
                style={{ marginBottom: 0 }}
                tabBarStyle={{ marginBottom: 0 }}
              />
            </div>
          )}
          <PageLayoutMainContent>
            <Suspense fallback={<LoadingSpinner />}>
              {activeTabKey === 'studio' && <ScopingToolPage />}
              {activeTabKey === 'devices' && <DeviceManagementPage />}
              {activeTabKey === 'demo' && <DemoPage />}
              {activeTabKey === 'live' && <LivePageContainer key={remountKey} />}
              {activeTabKey === 'historical' && (spaceId || buildingId) && (
                <Dashboard
                  key={remountKey}
                  isRoom={isRoom}
                  isZone={isZone}
                  spaceId={spaceId}
                  buildingId={buildingId}
                />
              )}
              <DetailsPanelWrapper isRoom={isRoom} isZone={isZone} />
            </Suspense>
          </PageLayoutMainContent>
        </>
      )}
      {!newFeedbackEnabled && (
        <AutoTooltip
          label="Share your feedback"
          className="absolute right-5 bottom-5 z-50"
          placement="bottom-end"
        >
          <a
            href="https://support.butlr.io/hc/en-us/requests/new"
            target="_blank"
            rel="noreferrer"
            aria-label="User feedback"
          >
            <UserFeedbackIcon className="text-content-primary hover:text-content-secondary" />
          </a>
        </AutoTooltip>
      )}
    </div>
  );
};

Mainboard.queries = {
  Building: gql`
    query MainboardBuilding($buildingId: ID!) {
      building(id: $buildingId) {
        id
        name
      }
    }
  `,
  Space: gql`
    query MainboardSpace($spaceId: String!) {
      floors(ids: [$spaceId]) {
        data {
          id: floor_id
          name
        }
      }
    }
  `,
  Room: gql`
    query MainboardRooms($roomId: String!) {
      rooms(ids: [$roomId]) {
        data {
          id: room_id
          name
        }
      }
    }
  `,
  Zone: gql`
    query MainboardZones($zoneId: String!) {
      zones(ids: [$zoneId]) {
        data {
          id: zone_id
          name
        }
      }
    }
  `,
};
